// App
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Components
import SharedMarketingBlurb from 'app/shared/modules/MobileSRP/SeoFooter/MarketingBlurb';
import SrpContentWrapper from 'app/shared/modules/MobileSRP/__BaseComponents__/SrpContentWrapper';
import MobileLinkGroup from 'app/shared/modules/navigation/footer/SeoFooter/MobileLinkGroup';

// Misc / utils
import searchSlugUtils from 'app/shared/utils/searchSlugUtils';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { SeoLink, SeoLinkGroup } from 'app/shared/flux/reducers/seo';
import * as S from './styles';

const logger = getGlobalLogger('modules/mobilesrp/seofooter');

const SeoFooter = () => {
  // Redux
  const seoLinks = useSelector((state: RootReduxState) => state.seo.seoFooterLinks);
  const pathname = useSelector((state: RootReduxState) => state.location.current.pathname);
  const slug = searchSlugUtils.getSearchSlugFromUrl(pathname);
  const shouldShowMarketingBlurb = slug === 'apartments-for-rent' || slug === 'houses-for-rent';

  // Filter out any links with /undefined/ in the URL to prevent displaying broken links
  const filteredSeoLinks = useMemo(() => {
    if (!seoLinks.groups) {
      return seoLinks;
    }

    const filteredGroups = seoLinks.groups
      .map((group: SeoLinkGroup) => {
        const validLinks = group.links.filter((link: SeoLink) => {
          const hasUndefinedInUrl = link.uriV2 && link.uriV2.includes('/undefined/');

          if (hasUndefinedInUrl) {
            logger?.warn('Filtered out SEO footer link with /undefined/ in URL', {
              pathname,
              linkName: link.name,
              linkUrl: link.uriV2,
              groupHeader: group.header,
            });
          }

          return !hasUndefinedInUrl;
        });

        return {
          ...group,
          links: validLinks,
        };
      })
      .filter((group: SeoLinkGroup) => group.links.length > 0); // Remove groups with no valid links

    return {
      ...seoLinks,
      groups: filteredGroups,
    };
  }, [seoLinks, pathname]);

  return (
    <SrpContentWrapper
      identifier="seo-footer"
      customHeader={
        shouldShowMarketingBlurb ? (
          <header>
            <SharedMarketingBlurb />
          </header>
        ) : null
      }
      background="backgroundGrey"
      fluid
      border="borderTop"
    >
      {shouldShowMarketingBlurb ? <S.SemanticDivider /> : null}
      <ul>
        {filteredSeoLinks.groups &&
          filteredSeoLinks.groups.map((group: SeoLinkGroup) => {
            const hasLinks = group.links.length > 0;

            if (!hasLinks) {
              return null;
            }

            return (
              hasLinks && (
                <MobileLinkGroup
                  header={group.header}
                  links={group.links}
                  key={`seo-footer-mobile-link-group-${group.header}`}
                />
              )
            );
          })}
      </ul>
    </SrpContentWrapper>
  );
};

export default SeoFooter;
